// src/pages/admin/MobileDashboard.jsx
// Native app-like mobile admin — bottom nav, camera, touch-optimized

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useBranding } from "../../hooks/useBranding";
import { usePWAInstall } from "../../hooks/useMobile";
import { logActivity, ROLE_LABELS, ROLE_COLORS } from "../../firebase/auth";
import {
  subscribeDesigns, fetchDesigns, subscribeCategories,
  addDesign, updateDesign, deleteDesign, toggleDesignActive,
} from "../../firebase/services";
import { B, fonts, Input, Spinner } from "../../components/UI";
import InstallBanner from "../../components/InstallBanner";

// ─── SVG Icons ────────────────────────────────────────────────────────────────
const IconDesigns = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="9" rx="1"/>
    <rect x="14" y="3" width="7" height="5" rx="1"/>
    <rect x="14" y="12" width="7" height="9" rx="1"/>
    <rect x="3" y="16" width="7" height="5" rx="1"/>
  </svg>
);

const IconSearch = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7"/>
    <line x1="16.5" y1="16.5" x2="22" y2="22"/>
  </svg>
);

const IconProfile = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4"/>
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
  </svg>
);

const IconPlus = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

function ImageNavButton({ direction, onClick, style = {} }) {
  const isPrev = direction === "prev";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={isPrev ? "Show previous image" : "Show next image"}
      style={{
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 34,
        height: 34,
        borderRadius: "50%",
        border: "1px solid rgba(255,255,255,0.22)",
        background: "rgba(17,24,39,0.58)",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        zIndex: 3,
        boxShadow: "0 8px 18px rgba(0,0,0,0.22)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        ...style,
      }}
    >
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d={isPrev ? "M12.5 4.5 7 10l5.5 5.5" : "M7.5 4.5 13 10l-5.5 5.5"}
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

// ─── Bottom Nav ───────────────────────────────────────────────────────────────
function BottomNav({ active, onChange, canAdd }) {
  return (
    <nav className="bottom-nav" style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: B.headerBg,
      borderTop: `1px solid rgba(255,255,255,0.08)`,
      display: "flex", alignItems: "center", height: 64,
      boxShadow: "0 -4px 24px rgba(0,0,0,0.5)",
    }}>

      {/* Designs */}
      <NavButton id="designs" active={active} onChange={onChange} label="Designs">
        <IconDesigns color={active === "designs" ? B.orange : "#94a3b8"} />
      </NavButton>

      {/* Center Add button */}
      {canAdd && (
        <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <button onClick={() => onChange("add")}
            style={{
              width: 54, height: 54, borderRadius: "50%", border: "none",
              background: active === "add"
                ? `linear-gradient(135deg, ${B.orangeLight}, ${B.orange})`
                : `linear-gradient(135deg, ${B.orange}, ${B.orangeDark})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", marginBottom: 20,
              boxShadow: `0 4px 20px ${B.orange}99, 0 0 0 4px ${B.headerBg}, 0 0 0 5px ${B.orange}44`,
              transform: active === "add" ? "scale(1.08)" : "scale(1)",
              transition: "all 0.2s",
            }}>
            <IconPlus />
          </button>
        </div>
      )}

      {/* Search */}
      <NavButton id="search" active={active} onChange={onChange} label="Search">
        <IconSearch color={active === "search" ? B.orange : "#94a3b8"} />
      </NavButton>

      {/* Profile */}
      <NavButton id="profile" active={active} onChange={onChange} label="Profile">
        <IconProfile color={active === "profile" ? B.orange : "#94a3b8"} />
      </NavButton>
    </nav>
  );
}

function NavButton({ id, active, onChange, label, children }) {
  const isActive = active === id;
  return (
    <button onClick={() => onChange(id)}
      style={{
        flex: 1, border: "none", background: "none",
        display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", gap: 4,
        padding: "8px 0 6px", cursor: "pointer",
        transition: "opacity 0.15s",
      }}
      onTouchStart={e => e.currentTarget.style.opacity = "0.7"}
      onTouchEnd={e   => e.currentTarget.style.opacity = "1"}
    >
      {/* Active indicator dot above icon */}
      <div style={{
        width: isActive ? 20 : 0, height: 3,
        background: B.orange, borderRadius: 2,
        marginBottom: 2, transition: "width 0.25s",
        position: "absolute", top: 0,
      }} />
      {children}
      <span style={{
        fontSize: 10, fontWeight: isActive ? 700 : 400,
        color: isActive ? B.orange : "#94a3b8",
        fontFamily: fonts.body, letterSpacing: "0.03em",
        transition: "color 0.2s",
      }}>
        {label}
      </span>
    </button>
  );
}

// ─── Top Bar ──────────────────────────────────────────────────────────────────
function TopBar({ title, onBack, rightAction }) {
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 100,
      background: B.headerBg, borderBottom: `1px solid ${B.orange}33`,
      padding: "14px 18px", display: "flex", alignItems: "center", gap: 12,
      boxShadow: "0 2px 16px rgba(0,0,0,0.3)",
    }}>
      {onBack && (
        <button onClick={onBack}
          style={{ background: "none", border: "none", color: B.orange, fontSize: 22, cursor: "pointer", padding: "2px 6px 2px 0", lineHeight: 1 }}>
          ‹
        </button>
      )}
      <h2 style={{ flex: 1, margin: 0, fontFamily: fonts.adminDisplay, fontSize: 18, fontWeight: 700, color: "rgb(224, 107, 79)" }}>
        {title}
      </h2>
      {rightAction}
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function MobileToast({ toast }) {
  if (!toast) return null;
  return (
    <div style={{
      position: "fixed", top: 70, left: 16, right: 16, zIndex: 999,
      background: toast.color || B.orange, color: "#fff",
      padding: "13px 18px", borderRadius: 12, fontWeight: 600, fontSize: 14,
      boxShadow: "0 8px 32px rgba(0,0,0,0.3)", animation: "fadeUp 0.3s",
      fontFamily: fonts.body, textAlign: "center",
    }}>
      {toast.msg}
    </div>
  );
}

// ─── Design Card (mobile) ─────────────────────────────────────────────────────
function MobileDesignCard({ design, onEdit, onToggle, canEdit }) {
  const [pressed, setPressed] = useState(false);
  const [imgIndex, setImgIndex] = useState(0);
  const images = design.images?.length ? design.images : design.imageUrl ? [design.imageUrl] : [];
  const img = images[imgIndex];

  const showPrevImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const showNextImage = (e) => {
    e.stopPropagation();
    setImgIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      style={{
        background: B.white, borderRadius: 16,
        border: `1.5px solid ${design.active ? B.borderLight : B.orangeMid}`,
        overflow: "hidden", opacity: design.active ? 1 : 0.65,
        transform: pressed ? "scale(0.98)" : "scale(1)",
        transition: "transform 0.15s",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
      }}>

      {/* Image */}
      <div style={{ position: "relative", paddingBottom: "120%", background: B.orangeFaint }}>
        {img
          ? <img src={img} alt={design.name} loading="lazy"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, opacity: 0.15 }}>👗</div>
        }
        {images.length > 1 && (
          <>
            <ImageNavButton direction="prev" onClick={showPrevImage} style={{ left: 10 }} />
            <ImageNavButton direction="next" onClick={showNextImage} style={{ right: 10 }} />
          </>
        )}
        {/* OOS overlay */}
        {!design.active && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ background: B.red, color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20, fontFamily: fonts.body }}>OUT OF STOCK</span>
          </div>
        )}
        {/* Badge */}
        {design.badge && (
          <div style={{ position: "absolute", top: 8, left: 8 }}>
            <span style={{ background: design.badge === "new" ? B.green : design.badge === "sale" ? B.red : B.gold, color: "#fff", fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 4, fontFamily: fonts.body, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              {design.badge}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: "12px 12px 14px" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: B.orange, marginBottom: 10, fontFamily: fonts.adminDisplay }}>
          {design.price}
        </div>
        {design.fabric && (
          <div style={{ display: "inline-block", marginBottom: 10, background: "#f1edff", color: "#5b45c8", border: "1px solid #d7cdfa", borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700, fontFamily: fonts.body }}>
            🧵 {design.fabric}
          </div>
        )}
        {design.design_number && (
          <div style={{ display: "inline-block", marginBottom: 10, background: "#f1edff", color: "#5b45c8", border: "1px solid #d7cdfa", borderRadius: 999, padding: "3px 10px", fontSize: 11, fontWeight: 700, fontFamily: fonts.body }}>
            D.No {design.design_number}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 8 }}>
          {canEdit && (
            <button onClick={() => onEdit(design)}
              style={{ flex: 1, background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 10, padding: "9px", fontSize: 12, fontWeight: 600, color: B.orange, cursor: "pointer", fontFamily: fonts.body }}>
              ✏️ Edit
            </button>
          )}
          <button onClick={() => onToggle(design)}
            style={{ flex: 1, background: design.active ? B.redLight : B.greenLight, border: `1.5px solid ${design.active ? B.red : B.green}33`, borderRadius: 10, padding: "9px", fontSize: 12, fontWeight: 600, color: design.active ? B.red : B.green, cursor: "pointer", fontFamily: fonts.body }}>
            {design.active ? "Mark OOS" : "✅ Restore"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Designs Screen ───────────────────────────────────────────────────────────
function DesignsScreen({ designs, loading, onEdit, onToggle, permissions, categories }) {
  const [filter, setFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);
  const FILTERS = [
    { id: "all",      label: "All"       },
    { id: "active",   label: "Active"    },
    { id: "oos",      label: "Out Stock" },
    { id: "new",      label: "New"       },
  ];

  const filtered = designs.filter(d => {
    if (categoryFilter !== "all" && d.categoryId !== categoryFilter) return false;
    if (filter === "active") return d.active;
    if (filter === "oos")    return !d.active;
    if (filter === "new")    return d.badge === "new";
    return true;
  });
  const pageSize = 12;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [filter, categoryFilter]);

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar title={`Designs (${designs.length})`} />

      {/* Filter chips */}
      <div style={{ padding: "14px 16px 8px", display: "flex", gap: 8, overflowX: "auto" }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            style={{ flexShrink: 0, background: filter === f.id ? B.orange : B.white, color: filter === f.id ? "#fff" : B.textMid, border: `1.5px solid ${filter === f.id ? B.orange : B.border}`, borderRadius: 20, padding: "6px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
            {f.label}
          </button>
        ))}
      </div>
      <div style={{ padding: "0 16px 8px" }}>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: B.text, fontFamily: fonts.body, outline: "none" }}>
          <option value="all">All categories</option>
          {categories.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 24px", color: B.textLight }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>👗</div>
          <p style={{ fontFamily: fonts.body }}>No designs here yet.</p>
        </div>
      ) : (
        <div style={{ padding: "8px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {paginated.map(d => (
            <MobileDesignCard key={d.id} design={d}
              onEdit={onEdit} onToggle={onToggle}
              canEdit={permissions?.canManageDesigns} />
          ))}
        </div>
      )}
      {!loading && filtered.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "12px 16px 0" }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ border: `1px solid ${B.border}`, background: B.white, borderRadius: 8, padding: "8px 10px", fontFamily: fonts.body, fontSize: 12, color: B.textMid }}>
            Previous
          </button>
          <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Page {page}/{totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ border: `1px solid ${B.border}`, background: B.white, borderRadius: 8, padding: "8px 10px", fontFamily: fonts.body, fontSize: 12, color: B.textMid }}>
            Next
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Search Screen ────────────────────────────────────────────────────────────
function SearchScreen({ designs, onEdit, onToggle, permissions }) {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const inputRef = useRef();

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 100); }, []);

  const results = query.length > 1
    ? designs.filter(d =>
        [d.designNo, d.price, d.colors, d.fabric, d.setType, d.setPieces, d.work, d.sizes].some(f =>
          f?.toLowerCase().includes(query.toLowerCase())
        ))
    : [];
  const pageSize = 12;
  const totalPages = Math.max(1, Math.ceil(results.length / pageSize));
  const paginated = results.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [query]);

  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar title="Search" />

      <div style={{ padding: "14px 16px" }}>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: B.textLight }}>🔍</span>
          <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Color, fabric, set..."
            style={{ width: "100%", background: B.white, border: `2px solid ${query ? B.orange : B.border}`, borderRadius: 50, padding: "13px 18px 13px 44px", fontSize: 15, color: B.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box", transition: "border 0.2s" }} />
        </div>
      </div>

      {query.length > 1 && (
        <div style={{ padding: "4px 16px 8px" }}>
          <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>{results.length} results</span>
        </div>
      )}

      <div style={{ padding: "0 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {paginated.map(d => (
          <MobileDesignCard key={d.id} design={d}
            onEdit={onEdit} onToggle={onToggle}
            canEdit={permissions?.canManageDesigns} />
        ))}
      </div>
      {query.length > 1 && results.length > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "12px 16px 0" }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            style={{ border: `1px solid ${B.border}`, background: B.white, borderRadius: 8, padding: "8px 10px", fontFamily: fonts.body, fontSize: 12, color: B.textMid }}>
            Previous
          </button>
          <span style={{ fontSize: 12, color: B.textLight, fontFamily: fonts.body }}>Page {page}/{totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            style={{ border: `1px solid ${B.border}`, background: B.white, borderRadius: 8, padding: "8px 10px", fontFamily: fonts.body, fontSize: 12, color: B.textMid }}>
            Next
          </button>
        </div>
      )}

      {query.length <= 1 && (
        <div style={{ textAlign: "center", padding: "60px 24px", color: B.textLight }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          <p style={{ fontFamily: fonts.body, fontSize: 14 }}>Type at least 2 characters to search</p>
        </div>
      )}
    </div>
  );
}

// ─── Add / Edit Design Screen ─────────────────────────────────────────────────
function AddEditScreen({ initial, categories, onDone, showToast, onSavingChange }) {
  const blank = { price: "", colors: "", fabric: "", setType: "", setPieces: "", work: "", sizes: "S,M,L,XL,XXL", kurtilength: "", bottomLength: "", dupattaDetails: "", categoryId: "", categorySlug: "", badge: "", design_number: "", visibleOnWebsite: false };
  const [form,         setForm]         = useState({ ...blank, ...(initial || {}) });
  const [existingImgs, setExistingImgs] = useState(initial?.images || (initial?.imageUrl ? [initial.imageUrl] : []));
  const [newFiles,     setNewFiles]     = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [step,         setStep]         = useState(1); // 1=photos, 2=details
  const fileInputRef = useRef();
  const cameraRef    = useRef();
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files);
    setNewFiles(prev => [...prev, ...picked]);
    e.target.value = "";
  };

  const handleCatChange = (id) => {
    const cat = categories.find(c => c.id === id);
    set("categoryId", id);
    set("categorySlug", cat?.slug || "");
  };

  const handleSave = async () => {
    if (!form.price) { showToast("Price is required", B.red); return; }
    setSaving(true);
    onSavingChange?.(true);
    try {
      if (initial) {
        await updateDesign(initial.id, form, existingImgs, newFiles);
        showToast("Design updated ✓");
      } else {
        await addDesign(form, newFiles);
        showToast("Design added ✓");
      }
      onDone();
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setSaving(false);
      onSavingChange?.(false);
    }
  };

  const totalImgs = existingImgs.length + newFiles.length;

  return (
    <div style={{ paddingBottom: 120 }}>
      <TopBar
        title={initial ? "Edit Design" : "Add Design"}
        onBack={onDone}
      />

      {/* Step indicators */}
      <div style={{ display: "flex", padding: "16px 20px 0", gap: 8 }}>
        {[1, 2].map(s => (
          <div key={s} onClick={() => setStep(s)}
            style={{ flex: 1, height: 4, borderRadius: 2, background: step >= s ? B.orange : B.borderLight, cursor: "pointer", transition: "background 0.3s" }} />
        ))}
      </div>
      <div style={{ display: "flex", padding: "6px 20px 16px" }}>
        {["Photos", "Details"].map((label, i) => (
          <div key={label} style={{ flex: 1, fontSize: 11, fontWeight: step === i + 1 ? 700 : 400, color: step === i + 1 ? B.orange : B.textLight, fontFamily: fonts.body }}>
            {label}
          </div>
        ))}
      </div>

      {/* ── STEP 1: Photos ── */}
      {step === 1 && (
        <div style={{ padding: "0 16px" }}>
          <p style={{ fontSize: 13, color: B.textMid, marginBottom: 16, fontFamily: fonts.body, lineHeight: 1.6 }}>
            Add photos of the design. You can take a photo directly with your camera or pick from gallery.
          </p>

          {/* Photo grid */}
          {totalImgs > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
              {existingImgs.map((src, i) => (
                <div key={`ex-${i}`} style={{ position: "relative", paddingBottom: "100%", borderRadius: 12, overflow: "hidden", border: `2px solid ${i === 0 ? B.orange : B.borderLight}` }}>
                  <img src={src} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  {i === 0 && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: B.orange, color: "#fff", fontSize: 9, textAlign: "center", padding: "2px 0", fontFamily: fonts.body }}>Primary</div>}
                  <button onClick={() => setExistingImgs(p => p.filter((_, j) => j !== i))}
                    style={{ position: "absolute", top: 4, right: 4, width: 24, height: 24, borderRadius: "50%", background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                </div>
              ))}
              {newFiles.map((file, i) => (
                <div key={`new-${i}`} style={{ position: "relative", paddingBottom: "100%", borderRadius: 12, overflow: "hidden", border: `2px solid ${existingImgs.length === 0 && i === 0 ? B.orange : "#25D366"}` }}>
                  <img src={URL.createObjectURL(file)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
                  <div style={{ position: "absolute", top: 4, left: 4, background: "#25D366", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>✓</div>
                  <button onClick={() => setNewFiles(p => p.filter((_, j) => j !== i))}
                    style={{ position: "absolute", top: 4, right: 4, width: 24, height: 24, borderRadius: "50%", background: "rgba(0,0,0,0.7)", color: "#fff", border: "none", fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Camera + Gallery buttons */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
            <button onClick={() => cameraRef.current?.click()}
              style={{ background: B.headerBg, border: `2px solid ${B.orange}44`, borderRadius: 14, padding: "20px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <span style={{ fontSize: 28 }}>📷</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: fonts.body }}>Camera</span>
              <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: fonts.body }}>Take photo now</span>
            </button>
            <button onClick={() => fileInputRef.current?.click()}
              style={{ background: B.headerBg, border: `2px solid ${B.orange}44`, borderRadius: 14, padding: "20px 12px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <span style={{ fontSize: 28 }}>🖼️</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", fontFamily: fonts.body }}>Gallery</span>
              <span style={{ fontSize: 10, color: "#94a3b8", fontFamily: fonts.body }}>Pick from phone</span>
            </button>
          </div>

          {/* Hidden inputs */}
          <input ref={cameraRef}    type="file" accept="image/*" capture="environment" onChange={handleFiles} style={{ display: "none" }} />
          <input ref={fileInputRef} type="file" accept="image/*" multiple            onChange={handleFiles} style={{ display: "none" }} />

          <button onClick={() => setStep(2)}
            style={{ width: "100%", background: B.orange, border: "none", borderRadius: 14, padding: "16px", fontSize: 15, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: fonts.body }}>
            Next: Add Details →
          </button>
        </div>
      )}

      {/* ── STEP 2: Details ── */}
      {step === 2 && (
        <div style={{ padding: "0 16px" }}>
          <MobileInput label="Price *"         value={form.price}    onChange={e => set("price",    e.target.value)} placeholder="e.g. ₹1,299" inputMode="text" />
          <MobileInput label="Design Number (optional)" value={form.design_number || ""} onChange={e => set("design_number", e.target.value)} placeholder="e.g. QTN-10045" />
          <MobileInput label="Colors"           value={form.colors}   onChange={e => set("colors",   e.target.value)} placeholder="e.g. Purple, Pink, Teal" />
          <MobileInput label="Fabric"           value={form.fabric}   onChange={e => set("fabric",   e.target.value)} placeholder="e.g. Pure Cotton" />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Set Type</label>
            <select value={form.setType || ""} onChange={e => set("setType", e.target.value)}
              style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, color: B.text, fontFamily: fonts.body, outline: "none", appearance: "auto" }}>
              <option value="">-- Select --</option>
              <option value="colors">Colors</option>
              <option value="sizes">Sizes</option>
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Set Pieces</label>
            <select value={form.setPieces || ""} onChange={e => set("setPieces", e.target.value)}
              style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, color: B.text, fontFamily: fonts.body, outline: "none", appearance: "auto" }}>
              <option value="">-- Select --</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
            </select>
          </div>
          <MobileInput label="Work"             value={form.work}     onChange={e => set("work",     e.target.value)} placeholder="e.g. Mirror Work" />
          <MobileInput label="Kurti Length"     value={form.kurtilength}  onChange={e => set("kurtilength",  e.target.value)} placeholder='e.g. 44"' />
          <MobileInput label="Bottom Length"    value={form.bottomLength} onChange={e => set("bottomLength", e.target.value)} placeholder='e.g. 38"' />
          <MobileInput label="Dupatta Details"  value={form.dupattaDetails} onChange={e => set("dupattaDetails", e.target.value)} placeholder="e.g. 2.25mt Mal Cotton" />
          <MobileInput label="Sizes"            value={form.sizes}    onChange={e => set("sizes",    e.target.value)} />

          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Frontend Visibility</label>
            <button
              type="button"
              onClick={() => set("visibleOnWebsite", !form.visibleOnWebsite)}
              style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: fonts.body, cursor: "pointer" }}
            >
              <span style={{ fontSize: 14, color: B.text }}>{form.visibleOnWebsite ? "Visible on website" : "Hidden from website"}</span>
              <span style={{ width: 42, height: 24, borderRadius: 999, background: form.visibleOnWebsite ? B.orange : B.borderLight, position: "relative", transition: "background 0.2s" }}>
                <span style={{ position: "absolute", top: 2, left: form.visibleOnWebsite ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </span>
            </button>
          </div>

          {/* Category */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Category</label>
            <select value={form.categoryId} onChange={e => handleCatChange(e.target.value)}
              style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, color: B.text, fontFamily: fonts.body, outline: "none", appearance: "auto" }}>
              <option value="">-- Select --</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {/* Badge */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>Badge</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[["", "None"], ["new", "🟢 New"], ["bestseller", "⭐ Best"], ["sale", "🔴 Sale"], ["featured", "🟠 Featured"]].map(([val, label]) => (
                <button key={val} onClick={() => set("badge", val)}
                  style={{ background: form.badge === val ? B.orange : B.white, color: form.badge === val ? "#fff" : B.textMid, border: `1.5px solid ${form.badge === val ? B.orange : B.border}`, borderRadius: 20, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: fonts.body }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <button onClick={handleSave} disabled={saving}
            style={{ width: "100%", background: saving ? B.orangeMid : B.orange, border: "none", borderRadius: 14, padding: "17px", fontSize: 16, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: fonts.body, marginBottom: 12 }}>
            {saving ? "Saving..." : initial ? "Save Changes ✓" : "Add Design ✓"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Profile Screen ───────────────────────────────────────────────────────────
function ProfileScreen({ profile, designs, onLogout, onSwitchToDesktop }) {
  const activeCount = designs.filter(d => d.active).length;
  return (
    <div style={{ paddingBottom: 100 }}>
      <TopBar title="Profile" />
      <div style={{ padding: "24px 16px" }}>

        {/* Avatar */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: `${ROLE_COLORS[profile?.role]}22`, border: `3px solid ${ROLE_COLORS[profile?.role]}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, margin: "0 auto 14px" }}>
            {profile?.name?.[0]?.toUpperCase() || "?"}
          </div>
          <div style={{ fontFamily: fonts.adminDisplay, fontSize: 20, fontWeight: 700, color: B.text, marginBottom: 4 }}>{profile?.name}</div>
          <div style={{ fontSize: 13, color: B.textLight, fontFamily: fonts.body, marginBottom: 8 }}>{profile?.email}</div>
          <span style={{ fontSize: 12, background: `${ROLE_COLORS[profile?.role]}18`, color: ROLE_COLORS[profile?.role], border: `1px solid ${ROLE_COLORS[profile?.role]}44`, padding: "3px 12px", borderRadius: 20, fontWeight: 700, fontFamily: fonts.body }}>
            {ROLE_LABELS[profile?.role]}
          </span>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 28 }}>
          {[
            ["👗", "Total Designs", designs.length, B.orange],
            ["✅", "Active",        activeCount,    B.green],
            ["🚫", "Out of Stock",  designs.length - activeCount, B.red],
          ].map(([icon, label, value, color]) => (
            <div key={label} style={{ background: B.white, border: `1px solid ${B.borderLight}`, borderRadius: 14, padding: "16px", borderLeft: `4px solid ${color}` }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color, fontFamily: fonts.adminDisplay }}>{value}</div>
              <div style={{ fontSize: 12, color: B.textMid, fontFamily: fonts.body }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Switch to desktop */}
        <button onClick={onSwitchToDesktop}
          style={{ width: "100%", background: B.orangeFaint, border: `1.5px solid ${B.orangeMid}`, borderRadius: 14, padding: "14px", fontSize: 14, fontWeight: 600, color: B.orange, cursor: "pointer", fontFamily: fonts.body, marginBottom: 12 }}>
          🖥️ Switch to Desktop View
        </button>

        {/* Logout */}
        <button onClick={onLogout}
          style={{ width: "100%", background: B.redLight, border: `1.5px solid ${B.red}33`, borderRadius: 14, padding: "14px", fontSize: 14, fontWeight: 600, color: B.red, cursor: "pointer", fontFamily: fonts.body }}>
          🚪 Logout
        </button>
      </div>
    </div>
  );
}

// ─── Mobile Input ─────────────────────────────────────────────────────────────
function MobileInput({ label, ...props }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: B.textMid, marginBottom: 8, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: fonts.body }}>{label}</label>}
      <input {...props}
        style={{ width: "100%", background: B.white, border: `1.5px solid ${B.border}`, borderRadius: 12, padding: "14px 16px", fontSize: 15, color: B.text, fontFamily: fonts.body, outline: "none", boxSizing: "border-box" }}
        onFocus={e => e.target.style.borderColor = B.orange}
        onBlur={e  => e.target.style.borderColor = B.border}
      />
    </div>
  );
}

// ─── MAIN MOBILE DASHBOARD ────────────────────────────────────────────────────
export default function MobileDashboard({ onSwitchToDesktop }) {
  const { profile, permissions, logout } = useAuth();
  const { branding }   = useBranding();
  const navigate       = useNavigate();
  const [screen,       setScreen]     = useState("designs"); // designs | add | search | profile | edit
  const [designs,      setDesigns]    = useState([]);
  const [categories,   setCategories] = useState([]);
  const [loading,      setLoading]    = useState(true);
  const [toast,        setToast]      = useState(null);
  const [editingDesign,setEditingDesign] = useState(null);
  const [pendingCrudOps, setPendingCrudOps] = useState(0);

  // Live designs only while the catalog is being viewed/managed (designs list +
  // search screens); on the add / profile / edit screens fetch the catalogue once
  // and cache it, so we never hold an always-on unbounded listener over all
  // ~500 design documents.
  const designsLoadedRef = useRef(false);
  useEffect(() => {
    if (screen === "designs" || screen === "search") {
      const u1 = subscribeDesigns(d => { setDesigns(d); setLoading(false); });
      return () => u1();
    }
    if (designsLoadedRef.current) return;
    setLoading(true);
    let active = true;
    (async () => {
      let list;
      try { list = await fetchDesigns(); }
      catch (e) { if (active) setLoading(false); return; }
      if (active) {
        designsLoadedRef.current = true;
        setDesigns(list);
        setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [screen]);

  useEffect(() => {
    const u2 = subscribeCategories(setCategories);
    return () => u2();
  }, []);

  const showToast = (msg, color = B.orange) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2800);
  };
  const setCrudBusy = isBusy => setPendingCrudOps(prev => isBusy ? prev + 1 : Math.max(0, prev - 1));
  const isCrudBusy = pendingCrudOps > 0;

  const handleToggle = async (design) => {
    setCrudBusy(true);
    try {
      await toggleDesignActive(design.id, !design.active, design.designNo);
      await logActivity(profile.uid, design.active ? "design_oos" : "design_restore", "Design status changed");
      showToast(design.active ? "Marked out of stock" : "Restored ✓", design.active ? B.red : B.green);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy(false);
    }
  };

  const handleEdit = (design) => {
    setEditingDesign(design);
    setScreen("edit");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin-login");
  };

  const activeTab = screen === "edit" ? "designs" : screen;

  return (
    <div style={{ minHeight: "100vh", background: B.cream, fontFamily: fonts.body }}>
      <MobileToast toast={toast} />
      <InstallBanner />

      {/* Screens */}
      {(screen === "designs") && (
        <DesignsScreen designs={designs} loading={loading} onEdit={handleEdit} onToggle={handleToggle} permissions={permissions} categories={categories} />
      )}
      {screen === "add" && (
        <AddEditScreen categories={categories} onDone={() => setScreen("designs")} showToast={showToast} onSavingChange={setCrudBusy} />
      )}
      {screen === "search" && (
        <SearchScreen designs={designs} onEdit={handleEdit} onToggle={handleToggle} permissions={permissions} />
      )}
      {screen === "profile" && (
        <ProfileScreen profile={profile} designs={designs} onLogout={handleLogout} onSwitchToDesktop={onSwitchToDesktop} />
      )}
      {screen === "edit" && editingDesign && (
        <AddEditScreen initial={editingDesign} categories={categories}
          onDone={() => { setScreen("designs"); setEditingDesign(null); }}
          showToast={showToast}
          onSavingChange={setCrudBusy} />
      )}

      {isCrudBusy && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(255, 255, 255, 0.82)",
          zIndex: 10000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backdropFilter: "blur(1px)",
        }}>
          <Spinner size={120} />
        </div>
      )}

      <BottomNav
        active={activeTab}
        onChange={s => { setScreen(s); setEditingDesign(null); }}
        canAdd={permissions?.canManageDesigns}
      />
    </div>
  );
}
